import { useFormContext, useWatch } from "react-hook-form";
import { ManualOrderFormValues } from "./useManualOrderForm";
import {
  BookerManyQuery,
  EnumProductType,
  ProductPurchaseRequest,
  useBookerCartPriceQuery,
  useDiscountByIdsQuery,
} from "gql/generated";
import { differenceInCalendarDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { Box, Flex } from "styled-system/jsx";
import { Table, Text } from "@ttc3k/trekker";
import {
  formatPrice,
  getDiscountNameString,
  getDiscountUnitInfoString,
  getMultilingualString,
  getPerTicketDiscountCount,
} from "shared/utils";
import { PricingSummary } from "shared/components/organisms/PricingSummary";
import { getProductTypeConfig } from "shared/config/productTypeConfig";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

type ManualOrderReviewProps = {
  allBookers: BookerManyQuery["bookerMany"];
};

export const ManualOrderReview = ({ allBookers }: ManualOrderReviewProps) => {
  const { control } = useFormContext<ManualOrderFormValues>();
  const email = useWatch({ control, name: "email" });
  const startISO = useWatch({ control, name: "startISO" });
  const endISO = useWatch({ control, name: "endISO" });
  const upsellProductIds = useWatch({
    control,
    name: "upsellProductIds",
  });
  const bookerId = useWatch({
    control,
    name: "bookerId",
  });
  const selectedBooker = useMemo(() => {
    return allBookers.find(b => b._id === bookerId);
  }, [allBookers, bookerId]);
  const mainProduct = useMemo(() => {
    return selectedBooker?.bundles[0]?.product;
  }, [selectedBooker]);

  const { t } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.MANUAL_ORDER.REVIEW",
  });

  const productPurchaseRequests: ProductPurchaseRequest[] = useMemo(
    () =>
      [
        {
          productId: mainProduct?._id ?? "",
          guestTiers: [],
          startDateISO: startISO,
          endDateISO: endISO,
        },
      ].concat(
        upsellProductIds.map(id => ({
          productId: id as string,
          guestTiers: [],
          startDateISO: "",
          endDateISO: "",
        })),
      ),
    [endISO, mainProduct?._id, startISO, upsellProductIds],
  );

  const { data } = useBookerCartPriceQuery({
    variables: {
      bookerId: bookerId,
      productPurchaseRequests: productPurchaseRequests,
    },
    skip: !bookerId || !mainProduct,
  });

  const productPrices =
    data?.bookerCartPrice?.productPrices.map(p => {
      const bundle = selectedBooker?.bundles.find(
        bun =>
          bun?.product?._id === p.productId ||
          bun?.upsells.some(u => u._id === p.productId),
      );
      const fullProduct =
        bundle && bundle.product?._id === p.productId
          ? bundle.product
          : bundle?.upsells.find(u => u._id === p.productId);

      return {
        _id: p.productId,
        price: p.price,
        name: fullProduct?.name
          ? getMultilingualString(fullProduct.name)
          : t("UNKNOWN_NAME"),
        type: fullProduct?.type ?? EnumProductType.Fee,
      };
    }) ?? [];

  const numberOfNights = differenceInCalendarDays(
    toZonedTime(endISO, "UTC"),
    toZonedTime(startISO, "UTC"),
  );

  const { data: discountsData } = useDiscountByIdsQuery({
    variables: {
      ids: data?.bookerCartPrice?.discountTotals.map(d => d.discountId),
    },
    skip: !data?.bookerCartPrice?.discountTotals,
  });

  const discountDisplayData =
    discountsData?.discountByIds.map(discount => {
      const total =
        data?.bookerCartPrice?.discountTotals.find(
          d => d.discountId === discount._id,
        )?.total ?? 0;
      return {
        label:
          getDiscountNameString(
            discount.name,
            discount.type,
            discount.promoCode,
          ) +
          " | " +
          getDiscountUnitInfoString(discount.unit, discount.amount, t("OFF"), {
            perTicket: !!discount.perTicket,
            perTicketTranslation: t("OFF_PER_TICKET"),
            flatRateTranslation: t("FLAT_RATE"),
            ticketCount: getPerTicketDiscountCount(discount, total),
          }),
        total,
        type: discount.type,
        isMainProductOnly: !!discount.excludeUpsells,
      };
    }) ?? [];

  const mainProductDiscounts = discountDisplayData.filter(
    d => d.isMainProductOnly,
  );
  const allProductDiscounts = discountDisplayData.filter(
    d => !d.isMainProductOnly,
  );

  const mainProductPrice = productPrices[0];
  const mainProductConfig = getProductTypeConfig(mainProductPrice?.type);

  return (
    <Flex flexDir={"column"} gap={"250"} minH={"280px"}>
      <Table.Root>
        <Table.Body>
          {/* Main Product */}
          {productPrices.length > 0 && (
            <Table.Row>
              <Table.Header paddingRight={"150"}>
                <Flex flexDir={"column"}>
                  <Text color={"text.dark"}>
                    {mainProductPrice.name}
                    {mainProductConfig.hasDates &&
                      ` • ${t("NIGHT", { count: numberOfNights })}`}
                  </Text>
                </Flex>
              </Table.Header>
              <Table.Cell>${formatPrice(mainProductPrice.price)}</Table.Cell>
            </Table.Row>
          )}

          {/* Main Product Discounts */}
          {mainProductDiscounts.map(discount => (
            <Table.Row key={discount.label}>
              <Table.Header paddingRight={"150"} lineClamp={2}>
                {discount.label}
              </Table.Header>
              <Table.Cell>-${formatPrice(discount.total)}</Table.Cell>
            </Table.Row>
          ))}

          {/* Upsell Products */}
          {productPrices.slice(1).map(v => (
            <Table.Row key={v._id}>
              <Table.Header paddingRight={"150"}>
                <Flex flexDir={"column"}>
                  <Text color={"text.dark"}>
                    {v.name}
                    {getProductTypeConfig(v.type).hasDates &&
                      ` • ${t("NIGHT", { count: numberOfNights })}`}
                  </Text>
                </Flex>
              </Table.Header>
              <Table.Cell>${formatPrice(v.price)}</Table.Cell>
            </Table.Row>
          ))}

          {/* All Product Discounts */}
          {allProductDiscounts.map(discount => (
            <Table.Row key={discount.label}>
              <Table.Header paddingRight={"150"} lineClamp={2}>
                {discount.label}
              </Table.Header>
              <Table.Cell>-${formatPrice(discount.total)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      <Box px={"150"}>
        <PricingSummary
          visual="panel"
          subtotal={data?.bookerCartPrice?.subTotal ?? 0}
          taxLines={data?.bookerCartPrice?.taxLines}
          taxTotal={data?.bookerCartPrice?.taxTotal ?? 0}
          customerFee={data?.bookerCartPrice?.customerFee ?? 0}
          total={data?.bookerCartPrice?.grandTotal ?? 0}
          labels={{
            subtotal: t("SUBTOTAL"),
            tax: t("TAX"),
            fee: t("FEE"),
            total: t("TOTAL"),
          }}
        />
      </Box>
      <Text visual={"smallRegular"} color={"text.mid"}>
        <Trans
          i18nKey="storefront:CALENDAR.MANUAL_ORDER.REVIEW.INVOICE_DESCRIPTION"
          components={{
            Text: <Text as={"span"} visual={"smallSemiBold"} />,
          }}
          values={{ email }}
        />
      </Text>
    </Flex>
  );
};
